import { TaskCategoryTaskType, TaskStepDto } from '@urbanx/agx.activities.client';
import {
  AgxDatePicker,
  AgxDivider,
  AgxSpacer,
  AgxTextInput,
  AgxTypography,
  AgxYesNoQuestion,
  YesNo,
} from '@urbanx/agx.ui-components2';
import dayjs from 'dayjs';
import TaskModalStepToggle from 'pages/allTasks/TaskModal/TaskModalStepToggle';
import { useEffect, useState } from 'react';
import ContractComplianceBodyCorporateRecord from './ContractComplianceBodyCorporateRecord';
import ContractComplianceBuildingAndPest from './ContractComplianceBuildingAndPest';
import ContractComplianceCoolingOffPeriod from './ContractComplianceCoolingOffPeriod';
import ContractComplianceDeposit from './ContractComplianceDeposit';
import ContractComplianceFinanceCondition from './ContractComplianceFinanceCondition';
import ContractComplianceSettlementDate from './ContractComplianceSettlementDate';
import ContractComplianceSpecialConditions from './ContractComplianceSpecialConditions';
import { ContractCompliancePageProps } from './ContractCompliancePageProps';
import {
  BALANCE_DEPOSIT_STEP_NAME,
  BODY_CORPORATE_RECORDS_STEP_NAME,
  BUILDING_AND_PEST_STEP_NAME,
  COOLING_OFF_PERIOD_STEP_NAME,
  FINANCE_CONDITION_STEP_NAME,
  INITIAL_DEPOSIT_STEP_NAME,
  REQUIRED_CONTRACT_DATE_STEP_NAME,
  REQUIRED_SALE_PRICE_STEP_NAME,
  SETTLEMENT_DATE_STEP_NAME,
  isSpecialConditionsComplianceStep,
} from './ContractComplianceHelper';

const CONTRACT_DATE_REQUIRED_ERROR = 'Contract date is required';
const SALE_PRICE_REQUIRED_ERROR = 'Sale price is required';

const contractCompliancePageLabels = [
  'Contract Compliance',
  'Special Conditions',
  'Contract Details',
  'Contract Dates',
];

type ContractComplianceStepProps = ContractCompliancePageProps & {
  onSpecialConditionsUpdated: NonNullable<
    ContractCompliancePageProps['onSpecialConditionsUpdated']
  >;
  step: TaskStepDto;
};

const ContractComplianceStep = (props: ContractComplianceStepProps) => {
  const {
    contractDate,
    hasSpecialConditions,
    onContractDateUpdated,
    onHasSpecialConditionsUpdated,
    onSalePriceUpdated,
    onSpecialConditionsUpdated,
    balanceDeposit,
    bodyCorporateRecordsCondition,
    buildingAndPestCondition,
    initialDeposit,
    settlementInfo,
    onFinanceConditionValidationErrorChange,
    onBuildingAndPestConditionUpdated,
    onBuildingAndPestValidationErrorChange,
    onBodyCorporateRecordsValidationErrorChange,
    onBodyCorporateRecordsConditionUpdated,
    onDepositValidationErrorChange,
    onDepositsUpdated,
    onSettlementDateValidationErrorChange,
    onSettlementInfoUpdated,
    onSubjectToFinanceUpdated,
    onWaiverOfCoolingOffUpdated,
    salePrice,
    specialConditions = [],
    step,
    subjectToFinance,
    toggleTaskStep,
    waiverOfCoolingOff,
  } = props;

  const isContractDateStep = step.name === REQUIRED_CONTRACT_DATE_STEP_NAME;
  const isSalePriceStep = step.name === REQUIRED_SALE_PRICE_STEP_NAME;
  const isInitialDepositStep = step.name === INITIAL_DEPOSIT_STEP_NAME;
  const isBalanceDepositStep = step.name === BALANCE_DEPOSIT_STEP_NAME;
  const isFinanceConditionStep = step.name === FINANCE_CONDITION_STEP_NAME;
  const isCoolingOffPeriodStep = step.name === COOLING_OFF_PERIOD_STEP_NAME;
  const isBuildingAndPestStep = step.name === BUILDING_AND_PEST_STEP_NAME;
  const isBodyCorporateRecordsStep = step.name === BODY_CORPORATE_RECORDS_STEP_NAME;
  const isSettlementDateStep = step.name === SETTLEMENT_DATE_STEP_NAME;
  const isSpecialConditionsStep = isSpecialConditionsComplianceStep(step.name);
  const specialConditionsAnswer = hasSpecialConditions ? YesNo.Yes : YesNo.No;
  const [showSpecialConditionsPicker, setShowSpecialConditionsPicker] =
    useState(hasSpecialConditions);

  useEffect(() => {
    setShowSpecialConditionsPicker(hasSpecialConditions ?? false);
  }, [hasSpecialConditions]);

  useEffect(() => {
    if (!isContractDateStep) return;

    const isCompleted = Boolean(contractDate);
    if ((step.isCompleted ?? false) !== isCompleted) {
      toggleTaskStep(step.order ?? 0, isCompleted);
    }
  }, [contractDate, isContractDateStep, step.isCompleted, step.order, toggleTaskStep]);

  useEffect(() => {
    if (!isSalePriceStep) return;

    const isCompleted = salePrice !== undefined && salePrice !== null;
    if ((step.isCompleted ?? false) !== isCompleted) {
      toggleTaskStep(step.order ?? 0, isCompleted);
    }
  }, [isSalePriceStep, salePrice, step.isCompleted, step.order, toggleTaskStep]);

  useEffect(() => {
    if (!isSpecialConditionsStep) return;

    const isCompleted =
      specialConditionsAnswer === YesNo.No ||
      (specialConditionsAnswer === YesNo.Yes && specialConditions.length > 0);

    if ((step.isCompleted ?? false) !== isCompleted) {
      toggleTaskStep(step.order ?? 0, Boolean(isCompleted));
    }
  }, [
    isSpecialConditionsStep,
    specialConditions.length,
    specialConditionsAnswer,
    step.isCompleted,
    step.order,
    toggleTaskStep,
  ]);

  if (isInitialDepositStep) {
    return (
      <ContractComplianceDeposit
        {...props}
        initialDeposit={initialDeposit}
        balanceDeposit={balanceDeposit}
        salePrice={salePrice}
        onDepositValidationErrorChange={onDepositValidationErrorChange}
        onDepositsUpdated={onDepositsUpdated}
      />
    );
  }

  if (isBalanceDepositStep) {
    return null;
  }

  if (isFinanceConditionStep) {
    return (
      <ContractComplianceFinanceCondition
        {...props}
        subjectToFinance={subjectToFinance}
        onFinanceConditionValidationErrorChange={onFinanceConditionValidationErrorChange}
        onSubjectToFinanceUpdated={onSubjectToFinanceUpdated}
      />
    );
  }

  if (isCoolingOffPeriodStep) {
    return (
      <ContractComplianceCoolingOffPeriod
        {...props}
        waiverOfCoolingOff={waiverOfCoolingOff}
        onWaiverOfCoolingOffUpdated={onWaiverOfCoolingOffUpdated}
      />
    );
  }

  if (isBuildingAndPestStep) {
    return (
      <ContractComplianceBuildingAndPest
        {...props}
        buildingAndPestCondition={buildingAndPestCondition}
        onBuildingAndPestValidationErrorChange={onBuildingAndPestValidationErrorChange}
        onBuildingAndPestConditionUpdated={onBuildingAndPestConditionUpdated}
      />
    );
  }

  if (isBodyCorporateRecordsStep) {
    return (
      <ContractComplianceBodyCorporateRecord
        {...props}
        bodyCorporateRecordsCondition={bodyCorporateRecordsCondition}
        onBodyCorporateRecordsValidationErrorChange={onBodyCorporateRecordsValidationErrorChange}
        onBodyCorporateRecordsConditionUpdated={onBodyCorporateRecordsConditionUpdated}
      />
    );
  }

  if (isSettlementDateStep) {
    return (
      <ContractComplianceSettlementDate
        {...props}
        settlementInfo={settlementInfo}
        onSettlementDateValidationErrorChange={onSettlementDateValidationErrorChange}
        onSettlementInfoUpdated={onSettlementInfoUpdated}
      />
    );
  }

  if (isContractDateStep) {
    const hasContractDate = Boolean(contractDate);

    return (
      <AgxDatePicker
        id="contract-compliance-contract-date"
        stretch
        label="Contract Date"
        required
        validate
        errorText={hasContractDate ? undefined : CONTRACT_DATE_REQUIRED_ERROR}
        value={contractDate ? dayjs(contractDate).format('YYYY-MM-DD') : ''}
        optionalText=""
        onChange={(value) => {
          onContractDateUpdated?.(value ? new Date(value) : undefined);
        }}
      />
    );
  }

  if (isSalePriceStep) {
    const hasSalePrice = salePrice !== undefined && salePrice !== null;

    return (
      <AgxTextInput
        id="contract-compliance-sale-price"
        type="currency"
        label="Sale Price"
        required
        hideOptionalText
        errorText={hasSalePrice ? undefined : SALE_PRICE_REQUIRED_ERROR}
        onChange={(updatedValue) => {
          onSalePriceUpdated?.(updatedValue ? parseFloat(updatedValue) || undefined : undefined);
        }}
        stretch
        defaultValue={salePrice?.toString() || ''}
        addTrailingZeros={false}
      />
    );
  }

  if (isSpecialConditionsStep) {
    return (
      <AgxSpacer direction="vertical" fill gap="spacing-xl">
        <AgxYesNoQuestion
          id="contract-compliance-special-conditions"
          label="Are there any Special Conditions?"
          defaultValue={specialConditionsAnswer}
          onValueChanged={({ value }) => {
            const hasConditions = value === YesNo.Yes;

            setShowSpecialConditionsPicker(hasConditions);
            onHasSpecialConditionsUpdated?.(hasConditions);

            if (value === YesNo.No) {
              void onSpecialConditionsUpdated([]);
            }
          }}
        />
        {showSpecialConditionsPicker && (
          <ContractComplianceSpecialConditions
            {...props}
            specialConditions={specialConditions}
            onSpecialConditionsUpdated={onSpecialConditionsUpdated}
          />
        )}
      </AgxSpacer>
    );
  }

  return <TaskModalStepToggle {...props} step={step} />;
};

const ContractCompliancePage = (props: Omit<ContractComplianceStepProps, 'step'>) => {
  if (props.task.category?.taskType !== TaskCategoryTaskType.ContractCompliance) {
    throw new Error('Contract Compliance pages require the canonical compliance task');
  }

  const taskSteps = props.task.taskSteps ?? [];
  const visibleTaskSteps = taskSteps.filter((step) => step.name !== BALANCE_DEPOSIT_STEP_NAME);

  return (
    <AgxSpacer direction="vertical" fill>
      <AgxTypography
        id="contractComplianceTaskTitle"
        type={'label'}
        size={'large'}
        color="var(--text-primary)"
        style={{ marginBottom: '32px' }}
      >
        {`Step ${props.taskNumber}: ${contractCompliancePageLabels[props.taskNumber - 1]}`}
      </AgxTypography>
      <AgxSpacer direction="vertical" fill gap="spacing-xl">
        {visibleTaskSteps.map((step, index) => (
          <AgxSpacer key={`${step.order}-${step.name}`} direction="vertical" fill gap="spacing-xl">
            <ContractComplianceStep {...props} step={step} />
            {index < visibleTaskSteps.length - 1 && <AgxDivider variant="horizontal" />}
          </AgxSpacer>
        ))}
      </AgxSpacer>
    </AgxSpacer>
  );
};

export default ContractCompliancePage;
